import React, { useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { getIncentiveTilePopUpData } from "../../../store/actions";
import CompanyGUIDs from "../../../utils/GUIDConst";
import { CompanyContext } from "../../../utils/CompanyContext";

const IncentiveTilePopUpModal = ({ clickedRow, showModal, toggleModal }) => {
  const dispatch = useDispatch();
  const { currentCompanyGuid } = useContext(CompanyContext);
  const { activeOrganizationInfo, incentiveData, isFetching } = useSelector(
    ({
      get_active_organization_detail: { data: activeOrg },
      get_incentive_tile_popup_data: { data, isFetching },
    }) => {
      return {
        activeOrganizationInfo: activeOrg || {},
        incentiveData: data?.[0] || {},
        isFetching: isFetching,
      };
    },
    shallowEqual,
  );
  const closeBtn = (
    <div className="links_right">
      <button className="modal_close" onClick={toggleModal} type="button">
        <img src="/images/close.svg" alt="close" />
      </button>
    </div>
  );

  useEffect(() => {
    if (!showModal) return;

    dispatch(
      getIncentiveTilePopUpData({
        companyguid: activeOrganizationInfo?.Companyguid,
        organization: activeOrganizationInfo?.Organization,
        primaryProgram: activeOrganizationInfo?.Program,
        employeeid: activeOrganizationInfo?.Employeeid,
        recMonth: clickedRow?.RecMonth,
        recYear: clickedRow?.RecYear,
        incentiveId: clickedRow?.IncentiveID,
      }),
    );
  }, [dispatch, showModal]);

  const {
    Organization = "",
    PrimaryProgram = "",
    IncentiveName = "",
  } = incentiveData;

  const orgUpper = Organization.toUpperCase();
  const programUpper = PrimaryProgram.toUpperCase();
  const validOrganizations = [
    "US SALES_SUPPORT - GDDY",
    "DEM SALES_SUPPORT - GDDY",
  ];

  const validPrograms = [
    "EMAIL SALES I",
    "EMAIL SALES I OJT",
    "EMAIL SALES II",
    "INBOUND SALES I",
    "INBOUND SALES I OJT",
    "INBOUND SALES II",
    "INBOUND SALES III",
    "WEBSITE & SECURITY SALES I OJT",
    "WEBSITE SECURITY SALES I",
    "WEBSITE SECURITY SALES II",
    "UK & IE INBOUND I",
    "UK & IE INBOUND II",
    "UK & IE SPECIALTY I",
    "UK & IE SPECIALTY II",
  ];
  const isEligible =
    validOrganizations.includes(orgUpper) &&
    validPrograms.includes(programUpper);

  const isIncentivePlanEligible =
    orgUpper === "DEM SALES_SUPPORT - GDDY" &&
    ["CDT CANADA", "CDT AUSTRALIA"].includes(programUpper);
  const incentivePlanLink = useMemo(() => {
    const name = IncentiveName.toLowerCase();

    if (name.includes("serbia") && name.includes("guide"))
      return "https://godaddy.service-now.com/gdsp?id=kb_article_view&sysparm_article=KB0018523";

    if (name.includes("serbia") && name.includes("sup"))
      return "https://godaddy.service-now.com/gdsp?id=kb_article_view&sysparm_article=KB0018690";

    if (name.includes("uk"))
      return "https://godaddy.service-now.com/gdsp?id=kb_article_view&sysparm_article=KB0018522";

    if (name.includes("us") && name.includes("guide"))
      return "https://godaddy.service-now.com/gdsp?id=kb_article_view&sysparm_article=KB0018504";

    if (name.includes("us") && name.includes("sup"))
      return "https://godaddy.service-now.com/gdsp?id=kb_article_view&sysparm_article=KB0018506";

    return isIncentivePlanEligible
      ? "https://godaddy.service-now.com/gdep?id=gdep_kb_article&sysparm_article=KB0018248"
      : "https://godaddy.service-now.com/gdep?id=gdep_kb_article&sysparm_article=KB0017409";
  }, [IncentiveName, isIncentivePlanEligible]);

  const headers = Object.keys(incentiveData)
    .filter((k) => k.endsWith("_HeaderName"))
    .sort(
      (a, b) =>
        incentiveData[a.replace("_HeaderName", "_DisplayOrder")] -
        incentiveData[b.replace("_HeaderName", "_DisplayOrder")],
    );

  const groupHeaders = headers.reduce((acc, key) => {
    const group = incentiveData[key.replace("_HeaderName", "_GroupName")];
    acc[group] = (acc[group] || 0) + 1;
    return acc;
  }, {});

  return (
    <Modal
      isOpen={showModal}
      toggle={toggleModal}
      backdrop={true}
      keyboard={true}
      id="insentive_tile-popup-modal"
      className="incentive-scroll-modal"
    >
      <ModalHeader
        toggle={toggleModal}
        close={closeBtn}
        className="custom-modal"
      ></ModalHeader>
      <ModalBody>
        <div className="incentive-table-wrapper">
          <table className="table table-bordered" id="sales_table_incentive">
            <thead>
              <tr>
                {Object.entries(groupHeaders).map(([grp, count]) => (
                  <th key={grp} colSpan={count}>
                    {grp}
                  </th>
                ))}
              </tr>
              <tr>
                {headers.map((h) => (
                  <th key={h}>{incentiveData[h]}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              <tr>
                {headers.map((h) => {
                  const base = h.replace("_HeaderName", "");
                  const value = incentiveData[`${base}_DisplayValue`];
                  const color = incentiveData[`${base}_Color`];

                  return (
                    <td key={base}>
                      {color && (
                        <span
                          className="bonus_circle"
                          style={{ backgroundColor: color }}
                        />
                      )}
                      {value || ""}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>

        {!isFetching &&
          (activeOrganizationInfo?.Companyguid || currentCompanyGuid) ===
            CompanyGUIDs["Go Daddy"] && (
            <div id="bonus_link" style={{ marginTop: "15px" }}>
              <div>
                <a
                  href={
                    isEligible
                      ? "https://us-west-2.quicksight.aws.amazon.com/sn/dashboards/848c8c80-3c3e-49e8-992b-6c2759ec4337"
                      : "https://us-west-2b.online.tableau.com/#/site/godaddy/views/SalesDetailsReport/SalesDetailsReport?:iid=2"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <b>Order Details</b>
                </a>
              </div>

              {IncentiveName && (
                <div style={{ marginTop: "10px" }}>
                  <a
                    href={incentivePlanLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <b>Incentive Plan Details</b>
                  </a>
                </div>
              )}
            </div>
          )}
      </ModalBody>
    </Modal>
  );
};
export default IncentiveTilePopUpModal;
